import { useEffect, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { Typography, RangePicker, DatePicker } from 'iglooform';
import { Tooltip } from 'antd';
import { useRequest } from 'umi';
import { Spin } from 'antd';
import { InfoFilled, AlertFilled } from 'iglooicon';
import moment, { Moment } from 'moment';
import { images } from '@/assets/images';
import { getStatistics } from '../service';
import Context from '@/utils/context';
import { ROLE } from '@/utils/role';
import OverviewCard from './components/overview-card';
import StatisticValue from './components/statistic-value';
import en from 'antd/es/date-picker/locale/en_US';
import vn from 'antd/es/date-picker/locale/vi_VN';
import styles from './index.less';

function getInitStartDate() {
  // 读缓存
  const startDateCache = localStorage.getItem(BUSINESS_OVERVIEW_STARTDATE);
  if (startDateCache) {
    return +startDateCache;
  }
  // 当月1号
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  return parseInt(String(new Date(year, month, 1).getTime() / 1000), 10);
}

function getInitEndDate() {
  // 读缓存
  const endDateCache = localStorage.getItem(BUSINESS_OVERVIEW_ENDDATE);
  if (endDateCache) {
    return +endDateCache;
  }
  // 当天
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const endDateStartDay = parseInt(
    String(new Date(year, month, day).getTime() / 1000),
    10,
  );
  // end date 需要是当天的23:59:59
  return (
    moment(endDateStartDay * 1000)
      .add(1, 'day')
      .unix() - 1
  );
}

const BusinessManagementOverview = () => {
  const { formatMessage } = useIntl();
  const { isPc } = useContext(Context);
  const [startDate, setStartDate] = useState(getInitStartDate());
  const [endDate, setEndDate] = useState(getInitEndDate());
  const currentLang = localStorage.getItem('selected_lang');

  const {
    data = {},
    run,
    loading,
  } = useRequest(getStatistics, {
    manual: true,
    formatResult: (res) => res,
  });

  useEffect(() => {
    // 请求
    run({
      start: startDate,
      end: endDate,
    });
  }, [startDate, endDate]);

  const teamMember = data?.team_member || [];

  const memberList = teamMember.map((item: { role: number; total: number }) => {
    const { role, total } = item;
    if (role === ROLE.agent) {
      return {
        text: formatMessage({ id: 'No. of Agent' }),
        value: total,
      };
    }
    if (role === ROLE.agentHead) {
      return {
        text: formatMessage({ id: 'No. of Agent Head' }),
        value: total,
      };
    }
    if (role === ROLE.businessManager) {
      return {
        text: formatMessage({ id: 'No. of Business Manager' }),
        value: total,
      };
    }
    if (role === ROLE.sbm) {
      return {
        text: formatMessage({ id: 'No. of Senior Business Manager' }),
        value: total,
      };
    }
  });

  const { commission, gwp, policy_sold } = data || {};

  const saleList = [
    {
      text: formatMessage({ id: 'Commission (VND)' }),
      value: <StatisticValue premium={commission} />,
    },
    {
      text: formatMessage({ id: 'Gross Written Premium (VND)' }),
      value: <StatisticValue premium={gwp} />,
    },
    {
      text: formatMessage({ id: 'Policy Sold' }),
      value: policy_sold,
    },
  ];

  const changeStartDateValue = (date: Moment) => {
    const startDate = Math.floor(+date.startOf('day') / 1000);
    setStartDate(startDate);
    // 写进缓存
    localStorage.setItem(BUSINESS_OVERVIEW_STARTDATE, String(startDate));
  };

  const changeEndDateValue = (date: Moment) => {
    const endDate = Math.floor(+date.endOf('day') / 1000);
    setEndDate(endDate);
    // 写进缓存
    localStorage.setItem(BUSINESS_OVERVIEW_ENDDATE, String(endDate));
  };

  const onChange = (dates: [Moment, Moment]) => {
    const [momentStartDate, momentEndDate] = dates;
    changeStartDateValue(momentStartDate);
    changeEndDateValue(momentEndDate);
  };

  const pcPicker = (
    <RangePicker
      className={styles.rangePicker}
      allowClear={false}
      value={[moment(startDate * 1000), moment(endDate * 1000)]}
      onChange={(dates) => onChange(dates as [Moment, Moment])}
      locale={currentLang === 'en-US' ? en : vn}
    />
  );

  const onChangeStartH5 = (date: Moment) => {
    changeStartDateValue(date);
  };

  const onChangeEndH5 = (date: Moment) => {
    changeEndDateValue(date);
  };

  const h5Picker = (
    <div className={styles.h5Picker}>
      <DatePicker
        suffixIcon={null}
        format="DD/MM/YYYY"
        allowClear={false}
        value={moment(startDate * 1000)}
        onChange={(date) => onChangeStartH5(date as Moment)}
        locale={currentLang === 'en-US' ? en : vn}
      />
      <div className={styles.h5PickerLine}></div>
      <DatePicker
        suffixIcon={null}
        format="DD/MM/YYYY"
        allowClear={false}
        value={moment(endDate * 1000)}
        onChange={(date) => onChangeEndH5(date as Moment)}
        locale={currentLang === 'en-US' ? en : vn}
      />
    </div>
  );

  const renderPicker = isPc ? pcPicker : h5Picker;

  return (
    <div className={styles.overview}>
      <div className={styles.overviewTitle}>
        <Typography level="h2" wrapElement="div">
          {formatMessage({ id: 'Team Statistics' })}
        </Typography>
        <div className={styles.rangePickerWrap}>
          {renderPicker}
          <Tooltip
            title={
              <Typography level="body2">
                {formatMessage({
                  id: 'The data is shown as from start date 00:00:00 to end date 23:59:59.',
                })}
              </Typography>
            }
            color="#fff"
            overlayInnerStyle={{ color: '#212121' }}
            trigger={isPc ? 'hover' : 'click'}
            placement={isPc ? 'top' : 'bottomRight'}
          >
            <AlertFilled style={{ fontSize: '24px', color: '#999' }} />
          </Tooltip>
        </div>
      </div>
      <Spin spinning={loading}>
        <div className={styles.overviewCardWrap}>
          <OverviewCard
            icon={images.team_icon}
            title={formatMessage({ id: 'Team Member' })}
            list={memberList}
          />
          <OverviewCard
            icon={images.total_premium_icon}
            title={formatMessage({ id: 'Team Sales' })}
            list={saleList}
          />
        </div>
      </Spin>
    </div>
  );
};

export default BusinessManagementOverview;
